/**
 * El teclado del teléfono, contado una vez para toda la aplicación.
 *
 * Al salir el teclado ni iOS ni Chrome (por defecto) encogen la ventana:
 * desplazan lo que se ve. Todo lo que medía «la ventana» —el shell a `100dvh`,
 * una hoja pegada a `bottom: 0`, el pie de un asistente de pasos— se quedaba
 * debajo de las teclas, o la página entera subía con un hueco y la cabecera
 * fuera. El viewport VISUAL sí encoge: es lo que de verdad queda por encima del
 * teclado, y lo que este módulo publica en `<html>`:
 *
 *   --app-height       alto de lo que se ve (px). Úsalo en vez de 100dvh.
 *   --keyboard-inset   cuánto tapa el teclado por abajo (px), 0 sin él.
 *   --viewport-top     cuánto ha desplazado el navegador lo que se ve (px).
 *   html.keyboard-open con el teclado fuera.
 *
 * Con `interactive-widget=resizes-content` en el viewport (Android), la ventana
 * sí encoge y el inset es 0; por eso «hay teclado» se decide contra el alto más
 * grande visto con este mismo ancho, no contra `innerHeight`.
 */

/** Más que esto solo puede ser un teclado: la barra del navegador no llega. */
const KEYBOARD_MIN = 120

let installed = false
const listeners = new Set()
const state = { height: 0, inset: 0, top: 0, open: false }

export function installViewportVars() {
  if (installed || typeof window === 'undefined') return
  installed = true

  const root = document.documentElement
  const viewport = window.visualViewport ?? null
  let fullHeight = 0
  let fullWidth = 0

  const update = () => {
    const height = viewport ? viewport.height : window.innerHeight
    const top = viewport ? viewport.offsetTop : 0
    // Girar el teléfono cambia el ancho: el alto completo de antes ya no vale.
    if (window.innerWidth !== fullWidth) { fullWidth = window.innerWidth; fullHeight = 0 }
    fullHeight = Math.max(fullHeight, window.innerHeight, height)

    state.height = Math.round(height)
    state.top = Math.round(top)
    state.inset = Math.max(0, Math.round(window.innerHeight - height - top))
    state.open = fullHeight - height > KEYBOARD_MIN && isTyping()

    root.style.setProperty('--app-height', `${state.height}px`)
    root.style.setProperty('--keyboard-inset', `${state.inset}px`)
    root.style.setProperty('--viewport-top', `${state.top}px`)
    root.classList.toggle('keyboard-open', state.open)
    for (const listener of listeners) listener(state)
  }

  viewport?.addEventListener('resize', update)
  viewport?.addEventListener('scroll', update)
  window.addEventListener('resize', update)
  // El foco llega antes que el teclado; el resize de después es el que cuenta,
  // pero al soltar el foco hay que volver aunque el navegador no avise.
  document.addEventListener('focusin', update)
  document.addEventListener('focusout', () => setTimeout(update, 50))
  update()
}

/** Si lo que tiene el foco es algo en que se escribe (y por tanto saca teclado). */
function isTyping() {
  const el = document.activeElement
  if (!el) return false
  if (el.isContentEditable) return true
  if (el.tagName === 'TEXTAREA') return true
  if (el.tagName !== 'INPUT') return false
  return !['button', 'checkbox', 'radio', 'range', 'color', 'file', 'submit', 'reset', 'image'].includes(el.type)
}

/** Para el que necesite saberlo en JS (llevar el hilo al final, por ejemplo). */
export function onViewportChange(listener) {
  listeners.add(listener)
  listener(state)
  return () => listeners.delete(listener)
}

export function viewportState() {
  return { ...state }
}

/**
 * La franja de la ventana que se ve, en las coordenadas de `position: fixed` y
 * de `getBoundingClientRect()` (las del viewport de layout).
 *
 * Para quien coloca una lista colgada de un control: con el teclado fuera en
 * iOS, `innerHeight` sigue contando las teclas como sitio libre, y «abre hacia
 * abajo, que cabe» la abría debajo de ellas. `layoutHeight` es la referencia de
 * un `bottom:` en píxeles.
 */
export function visibleBox() {
  const viewport = typeof window !== 'undefined' ? window.visualViewport : null
  const layoutHeight = typeof window !== 'undefined' ? window.innerHeight : 0
  const top = viewport ? viewport.offsetTop : 0
  const height = viewport ? viewport.height : layoutHeight
  return { top, bottom: top + height, height, layoutHeight }
}

/*
  La receta para lo que vive FUERA del armazón (teleportado a <body>) y mide la
  ventana. Dentro del armazón no hace falta: su `transform` lo hace contenedor
  de todo `position: fixed` que lleve dentro, que ya mide lo que se ve.

    pantalla entera   top: var(--viewport-top, 0px); bottom: auto;
                      height: var(--app-height, 100dvh);
    pegado abajo      bottom: var(--keyboard-inset, 0px);
    pegado arriba     top: calc(var(--viewport-top, 0px) + …);
*/
